import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import NotFound from "./NotFound";
import {
  ArrowLeft, Clock, TrendingUp, Mountain, BedDouble, Users, CalendarDays,
  Check, X, Loader2, Map as MapIcon, Mail, Compass, Play,
} from "lucide-react";
import { getTour, getTourExperience, getSavedExperience, mediaUrl } from "../lib/adventureApi";
import RALayout from "../components/ra/RALayout";
import EnquiryForm from "../components/ra/EnquiryForm";
import TourVideoReviews from "../components/ra/TourVideoReviews";
import ExperienceModal from "../components/ra/ExperienceModal";
import SaveExperienceDialog from "../components/ra/SaveExperienceDialog";
import { useMarket } from "../lib/market";
import { useSeo } from "../lib/seo";
import { RiderInfo, RideStats } from "../components/ra/RiderInfo";
import { SeasonBar, SEASON_COLORS } from "../components/ra/SeasonBar";

const SeasonRow = ({ color, label, value }) => (
  <div className="flex items-baseline gap-2.5 text-sm">
    <span className="w-2.5 h-2.5 rounded-full shrink-0 translate-y-0.5" style={{ background: color }} />
    <span className="font-semibold text-ink w-[190px] shrink-0">{label}</span>
    <span className="text-ink-secondary">{value}</span>
  </div>
);

const META = (tour) => [
  { icon: Clock, label: tour.days > 1 ? `${tour.days} days / ${tour.nights} nights` : "Day tour" },
  { icon: TrendingUp, label: tour.difficulty },
  ...(tour.altitude_max > 0 ? [{ icon: Mountain, label: `Max ${tour.altitude_max}m` }] : []),
  { icon: BedDouble, label: tour.accommodation },
  { icon: Users, label: tour.group_size },
  { icon: CalendarDays, label: tour.season },
];

export default function TourDetail() {
  const { slug: routeSlug, token } = useParams();
  const { price, priceNote } = useMarket();  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const autoPodcast = searchParams.get("podcast") === "1";
  const autoEnquire = searchParams.get("enquire") === "1";
  const [tour, setTour] = useState(null);
  useSeo({ title: tour && `${tour.name} | ${tour.days} days | Responsible Adventures`, description: (tour?.overview || tour?.subtitle || "").slice(0, 158), image: tour?.hero_image, path: tour && `/tours/${tour.slug}` });
  const [exp, setExp] = useState(null);
  const [expOpen, setExpOpen] = useState(false);
  const [saved, setSaved] = useState(null);   // persistent experience (partner-owned) when opened via /experience/:token
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    setTour(null); setExp(null); setSaved(null); setNotFound(false); setExpOpen(false);
    const start = async () => {
      let slug = routeSlug;
      if (token) {
        try {
          const s = await getSavedExperience(token);
          setSaved(s);
          slug = s.tour_slug;
        } catch (_) { setNotFound(true); return; }
      }
      try {
        const t = await getTour(slug);
        setTour(t);
        getTourExperience(slug).then((e) => { setExp(e); if (autoPodcast && t.destination === "nepal") setExpOpen(true); }).catch(() => {});
      } catch (_) { setNotFound(true); }
    };
    start();
  }, [routeSlug, token]);

  if (notFound) return <NotFound />;

  if (!tour) return (
    <RALayout><div className="flex items-center justify-center py-40 text-ink-secondary">
      <Loader2 className="w-6 h-6 animate-spin text-terracotta mr-2" /> Loading itinerary…
    </div></RALayout>
  );

  const builderRegion = "nepal";
  const openInBuilder = () => navigate(`/build/${builderRegion}?tour=${tour.slug}`);
  const branding = saved?.branding || null;

  return (
    <RALayout>
      <ExperienceModal exp={exp} tour={tour} open={expOpen} onOpenChange={setExpOpen} autoPodcast={autoPodcast} />
      {/* Persistent partner-owned / saved-experience ribbon */}
      {saved && (
        <div className="bg-moss text-white" data-testid="saved-experience-ribbon">
          <div className="max-w-6xl mx-auto px-5 sm:px-10 py-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
            <span className="font-mono text-[10px] uppercase tracking-widest bg-white/15 px-2 py-0.5 rounded-sm">Your saved experience</span>
            {saved.client_name && <span className="font-medium">Prepared for {saved.client_name}</span>}
            {branding?.company_name && <span className="text-white/80">by {branding.company_name}</span>}
            {saved.note && <span className="text-white/70 italic">“{saved.note}”</span>}
          </div>
        </div>
      )}
      {/* Hero */}
      <div className="relative h-[52vh] min-h-[360px] overflow-hidden" data-testid="tour-hero">
        <img src={tour.hero_image} alt={tour.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/45 to-ink/10" />
        <div className="absolute top-0 left-0 right-0 p-5 sm:p-8 max-w-6xl mx-auto w-full">
          <button onClick={() => navigate(-1)} data-testid="tour-back" className="flex items-center gap-2 text-white/90 hover:text-white text-sm font-medium">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 max-w-6xl mx-auto w-full">
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="font-mono text-xs tracking-[0.25em] uppercase text-white/70 mb-2">{tour.subtitle}</motion.p>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }}
            className="font-display font-extrabold text-4xl sm:text-6xl text-white tracking-tight leading-none" data-testid="tour-name">
            {tour.name}
          </motion.h1>
          {exp && tour.destination === "nepal" && (
            <motion.button initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.12 }}
              onClick={() => setExpOpen(true)} data-testid="feel-journey-btn"
              className="mt-5 inline-flex items-center gap-2.5 bg-terracotta hover:bg-terracotta-hover text-white pl-5 pr-6 h-12 rounded-full text-sm font-semibold tracking-wide shadow-2xl transition-colors">
              <Play className="w-4 h-4 fill-white" /> Feel the journey
            </motion.button>
          )}
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-5 sm:px-10 py-10 grid lg:grid-cols-3 gap-10">
        {/* Left */}
        <div className="lg:col-span-2 space-y-10">
          {/* Meta */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-4" data-testid="tour-meta">
            {META(tour).map((m, i) => {
              const Icon = m.icon;
              return (
                <div key={i} className="flex items-center gap-2.5 border border-sand-dark rounded-sm p-3 bg-white">
                  <Icon className="w-4 h-4 text-moss shrink-0" />
                  <span className="text-sm text-ink-secondary leading-tight">{m.label}</span>
                </div>
              );
            })}
          </div>

          {/* Best time to go */}
          {tour.season_summary && tour.season_bar && (
            <section className="border border-sand-dark rounded-lg bg-white p-5 sm:p-6" data-testid="tour-season">
              <div className="flex items-center gap-2 mb-4">
                <CalendarDays className="w-4 h-4 text-terracotta" />
                <h2 className="font-mono text-[11px] uppercase tracking-[0.25em] text-ink-secondary">Best time to go</h2>
              </div>
              <div className="space-y-2 mb-5">
                <SeasonRow color={SEASON_COLORS.best} label="Best" value={tour.season_summary.best} />
                <SeasonRow color={SEASON_COLORS.shoulder} label="Shoulder" value={tour.season_summary.shoulder} />
                <SeasonRow color={SEASON_COLORS.blockout} label="No scheduled departures" value={tour.season_summary.blockout} />
              </div>
              <SeasonBar bar={tour.season_bar} />
              <p className="text-xs text-ink-muted leading-relaxed mt-5 pt-4 border-t border-sand-dark">
                <span className="font-semibold text-ink-secondary">Why these dates? </span>
                Responsible Adventures selects departure periods based on weather, trail or road conditions,
                high-pass accessibility and the overall quality of your experience — not simply whether the
                destination is technically accessible.
              </p>
            </section>
          )}
          {tour.overview && (
            <section>
              <h2 className="font-display font-bold text-2xl tracking-tight mb-3">Overview</h2>
              <p className="text-ink-secondary leading-relaxed">{tour.overview}</p>
            </section>
          )}

          {/* Highlights */}
          {tour.highlights?.length > 0 && (
            <section>
              <h2 className="font-display font-bold text-2xl tracking-tight mb-3">Highlights</h2>
              <div className="flex flex-wrap gap-2">
                {tour.highlights.map((h) => (
                  <span key={h} className="text-sm bg-moss-light text-moss px-3 py-1.5 rounded-full font-medium">{h}</span>
                ))}
              </div>
            </section>
          )}

          {/* Enter the immersive experience (Nepal tours only; re-open anytime) */}
          {exp && tour.destination === "nepal" && (
            <button onClick={() => setExpOpen(true)} data-testid="open-experience-btn"
              className="group relative w-full overflow-hidden rounded-lg text-left" >
              <img src={tour.hero_image} alt="" className="absolute inset-0 w-full h-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/60 to-ink/30" />
              <div className="relative flex items-center gap-4 p-6 sm:p-7">
                <span className="w-14 h-14 shrink-0 rounded-full bg-terracotta flex items-center justify-center group-hover:scale-105 transition-transform">
                  <Play className="w-6 h-6 text-white ml-0.5" fill="currentColor" />
                </span>
                <div>
                  <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-terracotta">The experience</p>
                  <h2 className="font-display font-extrabold text-xl sm:text-2xl tracking-tight text-white leading-tight">Feel the journey before you go</h2>
                  <p className="text-sm text-white/70 mt-0.5">Watch it come to life — arrival, culture, food, films &amp; the podcast, all in one place.</p>
                </div>
              </div>
            </button>
          )}

          {/* Itinerary */}
          <RideStats stats={tour.ride_stats} />
          <section data-testid="tour-itinerary">
            <h2 className="font-display font-bold text-2xl tracking-tight mb-5">Day by day</h2>
            {tour.itinerary?.length > 0 ? (
              <div className="space-y-0">
                {tour.itinerary.map((row, i) => (
                  <div key={i} className="flex gap-4">
                    <div className="flex flex-col items-center">
                      <div className="w-10 h-10 rounded-full bg-terracotta text-white flex items-center justify-center font-mono text-sm font-bold shrink-0">{row.day}</div>
                      {i < tour.itinerary.length - 1 && <div className="w-px flex-1 bg-sand-dark my-1" />}
                    </div>
                    <div className="flex-1 pb-7">
                      <h3 className="font-display font-bold text-lg tracking-tight leading-snug">{row.title}</h3>
                      {row.desc && <p className="text-sm text-ink-secondary leading-relaxed mt-1.5">{row.desc}</p>}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="border border-dashed border-sand-dark rounded-md p-6 bg-white text-ink-muted text-sm" data-testid="tour-itinerary-pending">
                The full day-by-day itinerary for this tour is being added. Contact us for the complete plan in the meantime.
              </div>
            )}
          </section>

          <TourVideoReviews tourSlug={tour.slug} />

          {/* Gallery */}
          {tour.gallery?.length > 1 && (
            <section data-testid="tour-gallery">
              <h2 className="font-display font-bold text-2xl tracking-tight mb-4">Gallery</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {tour.gallery.map((src, i) => (
                  <div key={i} className="aspect-[4/3] overflow-hidden rounded-md">
                    <img src={src} alt={`${tour.name} ${i + 1}`} loading="lazy"
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                  </div>
                ))}
              </div>
            </section>
          )}

          <RiderInfo tour={tour} />

          {/* Inclusions / Exclusions */}
          {(tour.inclusions?.length > 0 || tour.exclusions?.length > 0) && (
            <section className="grid sm:grid-cols-2 gap-8">
              <div>
                <h2 className="font-display font-bold text-lg tracking-tight mb-3">What's included</h2>
                <ul className="space-y-1.5">
                  {tour.inclusions.map((inc) => (
                    <li key={inc} className="flex items-start gap-2 text-sm text-ink-secondary"><Check className="w-4 h-4 text-moss shrink-0 mt-0.5" />{inc}</li>
                  ))}
                </ul>
              </div>
              <div>
                <h2 className="font-display font-bold text-lg tracking-tight mb-3">Not included</h2>
                <ul className="space-y-1.5">
                  {tour.exclusions.map((ex) => (
                    <li key={ex} className="flex items-start gap-2 text-sm text-ink-muted"><X className="w-4 h-4 shrink-0 mt-0.5" />{ex}</li>
                  ))}
                </ul>
              </div>
            </section>
          )}
        </div>

        {/* Right sticky CTA */}
        <div className="lg:col-span-1">
          <div className="sticky top-24 border border-sand-dark rounded-md bg-white p-6" data-testid="tour-cta">
            {branding ? (
              <div className="flex items-center gap-3 pb-4 mb-4 border-b border-sand-dark" data-testid="experience-branding">
                {branding.logo_url && <img src={mediaUrl(branding.logo_url)} alt={branding.company_name} className="h-9 w-auto object-contain" />}
                <div className="min-w-0">
                  <p className="font-mono text-[10px] uppercase tracking-widest text-moss">Curated for you by</p>
                  <p className="font-display font-bold text-sm tracking-tight truncate">{branding.company_name}</p>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-2 text-moss mb-1"><Compass className="w-4 h-4" /><span className="font-mono text-xs uppercase tracking-widest">Responsible Adventures</span></div>
            )}
            <h3 className="font-display font-bold text-xl tracking-tight leading-snug">{tour.name}</h3>
            <p className="text-sm text-ink-muted mt-1">{tour.days > 1 ? `${tour.days} days` : "Day tour"} · {tour.difficulty}</p>

            <div className="mt-4 pt-4 border-t border-sand-dark" data-testid="tour-detail-price">
              {tour.price_from ? (
                <div className="flex items-baseline gap-2">
                  <span className="text-sm text-ink-muted">From</span>
                  <span className="font-display font-extrabold text-3xl tracking-tight text-terracotta">{price(tour.price_from)}</span>
                  <span className="text-sm text-ink-muted">/ person</span>
                </div>
              ) : (
                <p className="text-sm text-ink-secondary">Price on request</p>
              )}
              {tour.price_from && priceNote && (
                <p className="text-[11px] text-ink-muted mt-0.5" data-testid="tour-detail-price-note">{priceNote}</p>
              )}
            </div>

            {/* Primary action for a direct client: enquire / reserve a place */}
            <EnquiryForm destination={tour.name} tourSlug={tour.slug} bikeHire={tour.category === "cycling" ? (tour.bike_hire || []) : null} defaultOpen={autoEnquire} triggerLabel="Enquire & reserve your place"
              triggerClassName="w-full mt-5 flex items-center justify-center gap-2 bg-terracotta hover:bg-terracotta-hover text-white h-12 rounded-sm font-semibold tracking-wide transition-colors" />
            <p className="flex items-center justify-center gap-1.5 text-[11px] text-ink-muted mt-2">
              <Mail className="w-3 h-3 text-moss" /> No obligation — Raj &amp; team reply personally
            </p>

            {/* Keep the experience — persistent, shareable, partner-owned */}
            <SaveExperienceDialog tourSlug={tour.slug} tourName={tour.name}
              triggerClassName="w-full mt-3 flex items-center justify-center gap-2 border border-sand-dark hover:border-terracotta h-11 rounded-sm font-medium text-sm transition-colors" />

            {/* Secondary: customise on the map (trade tool) */}
            <button onClick={openInBuilder} data-testid="open-in-builder-btn"
              className="w-full mt-3 flex items-center justify-center gap-1.5 text-sm text-ink-secondary hover:text-terracotta transition-colors">
              <MapIcon className="w-4 h-4" /> Prefer to customise? Build &amp; price on the map
            </button>

            {!branding && (
              <p className="text-[11px] text-ink-muted mt-4 pt-4 border-t border-sand-dark text-center">Trade partner? <Link to="/trade" className="text-terracotta hover:underline">Log in for your commission &amp; branded proposals</Link>.</p>
            )}
          </div>
        </div>
      </div>
    </RALayout>
  );
}
